import { useState } from 'react'
import { useRouter } from 'next/router'
import { ICartSelection, IField, IFieldOption } from '@/client/types'
import { LabelTypes, Selection, UploadedFileSummary } from './types'

export const getOptionValue = (options: IFieldOption[], selection: Selection, key: string): string => {
  if (key === 'diameter') {
    return `${selection.diameter}mm`
  }

  if (key === 'dimension') {
    return `${selection.width}x${selection.height}mm`
  }

  if (key === 'variation') {
    return `${selection.sorts}`
  }

  if (key === 'quantity') {
    return `${selection.quantity}`
  }

  if (key === 'number_of_sku') {
    return `${selection.number_of_sku}`
  }

  const item = options.find((option) => option.id === selection[key]) as IFieldOption
  return item?.name
}

export const getOptionValueForShortSummary = (selection: ICartSelection, key: string): string => {
  if (key === 'diameter') {
    return `${selection?.diameter}mm`
  }

  if (key === 'dimension') {
    return `${selection?.width}x${selection?.height}mm`
  }

  if (key === 'quantity') {
    return `${selection?.quantity}`
  }
  return ''
}

export const getOptionsField = (fields: IField[] | undefined, key: string): IFieldOption[] => {
  const item = fields?.find((field) => field.key === key)
  return item?.options ?? []
}

export const updateInitialSelection = (selection) => {
  const newSelection = { ...selection }
  newSelection.transparency_type = 1
  newSelection.width = 30
  newSelection.height = 30
  newSelection.number_of_sku = 1
  newSelection.upload_completed = false
  newSelection.express_production = false
  newSelection.order_comment = newSelection.order_comment || newSelection['Order Comment']
  newSelection.order_name = newSelection.order_name || newSelection['Order name']
  newSelection.labels_on_roll = newSelection.labels_on_roll || newSelection['labels per roll']
  newSelection.max_roll_diameter = newSelection.max_roll_diameter || newSelection['max. roll diameter']
  return newSelection
}

export const isExcludingField = (excludes, selection: ICartSelection): boolean => {
  if (!excludes) return true
  const isExcluded = excludes.some(({ id, value }) => selection?.[id] === value)
  return !isExcluded
}

export const getCorrectDimensionType = (selection: ICartSelection, calculatorFields): string => {
  const excludeOfDiameter = isExcludingField(
    calculatorFields?.find((field) => field.key === 'diameter')?.exclude,
    selection
  )
  const excludeOfDimension = isExcludingField(
    calculatorFields?.find((field) => field.key === 'dimension')?.exclude,
    selection
  )
  if (excludeOfDiameter) return 'diameter'
  if (excludeOfDimension) return 'dimension'
  return ''
}

export const isInvalidConfigurations = (selection) => {
  const type = selection.transparency_type
  const skuConfig = selection.sku_config

  //ean field should have either 12 or 13 characters
  const isValidEan13 = (ean) => !!ean && (ean.length === 12 || ean.length === 13)

  const isValid = (fields, type) => {
    const hasSkuInput = !!fields.sku
    const hasEanInput = !!fields.ean
    const hasBrandNameInput = !!fields.brand_name

    switch (type) {
      case LabelTypes.LABEL_TYPE_3:
        return hasSkuInput && isValidEan13(fields.ean)

      case LabelTypes.LABEL_TYPE_6:
        return hasBrandNameInput && hasEanInput && fields.ean.length <= 10

      case LabelTypes.LABEL_TYPE_4:
        if (fields.type4_bctype === 'ean13') {
          return hasSkuInput && isValidEan13(fields.ean)
        }

        return hasSkuInput && hasEanInput
      case LabelTypes.LABEL_TYPE_8:
        if (fields.type4_bctype === 'ean13') {
          return hasSkuInput && isValidEan13(fields.ean)
        }
        return hasSkuInput && hasEanInput
      default:
        return hasSkuInput
    }
  }

  const shouldDisable = skuConfig.some((fields) => !isValid(fields, type))
  return shouldDisable
}

// Custom hook
export const useSharedState = () => {
  const { defaultLocale } = useRouter()
  const [tpyLang, setTpyLang] = useState(
    !defaultLocale ? 'en' : defaultLocale === 'nl' || defaultLocale === 'uk' ? 'en' : defaultLocale
  )
  const [tpyCta, setTpyCta] = useState('0')
  const [tpyLayout, setTpyLayout] = useState('a')
  const [tpyPaper, setTpyPaper] = useState('')
  const [uploadedFilesDetailsFromSummary, setUploadedFilesDetails] = useState<UploadedFileSummary[]>([])

  return {
    tpyLang,
    setTpyLang,
    tpyCta,
    setTpyCta,
    tpyLayout,
    setTpyLayout,
    tpyPaper,
    setTpyPaper,
    uploadedFilesDetailsFromSummary,
    setUploadedFilesDetails
  }
}

//for image preview
export const ImageDisplay = ({ tpy_type, tpy_lang, tpy_cta, tpy_layout, paper }) => {
  const imageMapping = {
    '1_de_0_a': 'tpy1_de_cta0_a',
    '1_de_0_b': 'tpy1_de_cta0_b',
    '1_de_1_a': 'tpy1_de_cta1_a',
    '1_de_1_b': 'tpy1_de_cta1_b',
    '1_de_0_a_3': 'tpy1_de_cta0_a_paper3',
    '1_de_0_b_3': 'tpy1_de_cta0_b_paper3',
    '1_de_1_a_3': 'tpy1_de_cta1_a_paper3',
    '1_de_1_b_3': 'tpy1_de_cta1_b_paper3',
    '1_es_0_a': 'tpy1_es_cta0_a',
    '1_es_0_b': 'tpy1_es_cta0_b',
    '1_es_1_a': 'tpy1_es_cta1_a',
    '1_es_1_b': 'tpy1_es_cta1_b',
    '1_es_0_a_3': 'tpy1_es_cta0_a_paper3',
    '1_es_0_b_3': 'tpy1_es_cta0_b_paper3',
    '1_es_1_a_3': 'tpy1_es_cta1_a_paper3',
    '1_es_1_b_3': 'tpy1_es_cta1_b_paper3',
    '1_fr_0_a': 'tpy1_fr_cta0_a',
    '1_fr_0_b': 'tpy1_fr_cta0_b',
    '1_fr_1_a': 'tpy1_fr_cta1_a',
    '1_fr_1_b': 'tpy1_fr_cta1_b',
    '1_fr_0_a_3': 'tpy1_fr_cta0_a_paper3',
    '1_fr_0_b_3': 'tpy1_fr_cta0_b_paper3',
    '1_fr_1_a_3': 'tpy1_fr_cta1_a_paper3',
    '1_fr_1_b_3': 'tpy1_fr_cta1_b_paper3',
    '1_it_0_a': 'tpy1_it_cta0_a',
    '1_it_0_b': 'tpy1_it_cta0_b',
    '1_it_1_a': 'tpy1_it_cta1_a',
    '1_it_1_b': 'tpy1_it_cta1_b',
    '1_it_0_a_3': 'tpy1_it_cta0_a_paper3',
    '1_it_0_b_3': 'tpy1_it_cta0_b_paper3',
    '1_it_1_a_3': 'tpy1_it_cta1_a_paper3',
    '1_it_1_b_3': 'tpy1_it_cta1_b_paper3',
    '1_en_0_a': 'tpy1_en_cta0_a',
    '1_en_1_a': 'tpy1_en_cta1_a',
    '1_en_0_a_3': 'tpy1_en_cta0_a_paper3',
    '1_en_1_a_3': 'tpy1_en_cta1_a_paper3',

    '2_de_0_a': 'tpy2_de_cta0_a',
    '2_de_0_b': 'tpy2_de_cta0_b',
    '2_de_1_a': 'tpy2_de_cta1_a',
    '2_de_1_b': 'tpy2_de_cta1_b',
    '2_de_0_a_3': 'tpy2_de_cta0_a_paper3',
    '2_de_0_b_3': 'tpy2_de_cta0_b_paper3',
    '2_de_1_a_3': 'tpy2_de_cta1_a_paper3',
    '2_de_1_b_3': 'tpy2_de_cta1_b_paper3',
    '2_es_0_a': 'tpy2_es_cta0_a',
    '2_es_0_b': 'tpy2_es_cta0_b',
    '2_es_1_a': 'tpy2_es_cta1_a',
    '2_es_1_b': 'tpy2_es_cta1_b',
    '2_es_0_a_3': 'tpy2_es_cta0_a_paper3',
    '2_es_0_b_3': 'tpy2_es_cta0_b_paper3',
    '2_es_1_a_3': 'tpy2_es_cta1_a_paper3',
    '2_es_1_b_3': 'tpy2_es_cta1_b_paper3',
    '2_fr_0_a': 'tpy2_fr_cta0_a',
    '2_fr_0_b': 'tpy2_fr_cta0_b',
    '2_fr_1_a': 'tpy2_fr_cta1_a',
    '2_fr_1_b': 'tpy2_fr_cta1_b',
    '2_fr_0_a_3': 'tpy2_fr_cta0_a_paper3',
    '2_fr_0_b_3': 'tpy2_fr_cta0_b_paper3',
    '2_fr_1_a_3': 'tpy2_fr_cta1_a_paper3',
    '2_fr_1_b_3': 'tpy2_fr_cta1_b_paper3',
    '2_it_0_a': 'tpy2_it_cta0_a',
    '2_it_0_b': 'tpy2_it_cta0_b',
    '2_it_1_a': 'tpy2_it_cta1_a',
    '2_it_1_b': 'tpy2_it_cta1_b',
    '2_it_0_a_3': 'tpy2_it_cta0_a_paper3',
    '2_it_0_b_3': 'tpy2_it_cta0_b_paper3',
    '2_it_1_a_3': 'tpy2_it_cta1_a_paper3',
    '2_it_1_b_3': 'tpy2_it_cta1_b_paper3',
    '2_en_0_a': 'tpy2_en_cta0_a',
    '2_en_1_a': 'tpy2_en_cta1_a',
    '2_en_0_a_3': 'tpy2_en_cta0_a_paper3',
    '2_en_1_a_3': 'tpy2_en_cta1_a_paper3',

    '3_de_0_a': 'tpy3_de_cta0_a',
    '3_de_0_b': 'tpy3_de_cta0_b',
    '3_de_1_a': 'tpy3_de_cta1_a',
    '3_de_1_b': 'tpy3_de_cta1_b',
    '3_de_0_a_3': 'tpy3_de_cta0_a_paper3',
    '3_de_0_b_3': 'tpy3_de_cta0_b_paper3',
    '3_de_1_a_3': 'tpy3_de_cta1_a_paper3',
    '3_de_1_b_3': 'tpy3_de_cta1_b_paper3',
    '3_es_0_a': 'tpy3_es_cta0_a',
    '3_es_0_b': 'tpy3_es_cta0_b',
    '3_es_1_a': 'tpy3_es_cta1_a',
    '3_es_1_b': 'tpy3_es_cta1_b',
    '3_es_0_a_3': 'tpy3_es_cta0_a_paper3',
    '3_es_0_b_3': 'tpy3_es_cta0_b_paper3',
    '3_es_1_a_3': 'tpy3_es_cta1_a_paper3',
    '3_es_1_b_3': 'tpy3_es_cta1_b_paper3',
    '3_fr_0_a': 'tpy3_fr_cta0_a',
    '3_fr_0_b': 'tpy3_fr_cta0_b',
    '3_fr_1_a': 'tpy3_fr_cta1_a',
    '3_fr_1_b': 'tpy3_fr_cta1_b',
    '3_fr_0_a_3': 'tpy3_fr_cta0_a_paper3',
    '3_fr_0_b_3': 'tpy3_fr_cta0_b_paper3',
    '3_fr_1_a_3': 'tpy3_fr_cta1_a_paper3',
    '3_fr_1_b_3': 'tpy3_fr_cta1_b_paper3',
    '3_it_0_a': 'tpy3_it_cta0_a',
    '3_it_0_b': 'tpy3_it_cta0_b',
    '3_it_1_a': 'tpy3_it_cta1_a',
    '3_it_1_b': 'tpy3_it_cta1_b',
    '3_it_0_a_3': 'tpy3_it_cta0_a_paper3',
    '3_it_0_b_3': 'tpy3_it_cta0_b_paper3',
    '3_it_1_a_3': 'tpy3_it_cta1_a_paper3',
    '3_it_1_b_3': 'tpy3_it_cta1_b_paper3',
    '3_en_0_a': 'tpy3_en_cta0_a',
    '3_en_1_a': 'tpy3_en_cta1_a',
    '3_en_0_a_3': 'tpy3_en_cta0_a_paper3',
    '3_en_1_a_3': 'tpy3_en_cta1_a_paper3',

    '4_de_0_a': 'tpy4_de_cta0_a',
    '4_de_0_b': 'tpy4_de_cta0_b',
    '4_de_1_a': 'tpy4_de_cta1_a',
    '4_de_1_b': 'tpy4_de_cta1_b',
    '4_de_0_a_3': 'tpy4_de_cta0_a_paper3',
    '4_de_0_b_3': 'tpy4_de_cta0_b_paper3',
    '4_de_1_a_3': 'tpy4_de_cta1_a_paper3',
    '4_de_1_b_3': 'tpy4_de_cta1_b_paper3',
    '4_es_0_a': 'tpy4_es_cta0_a',
    '4_es_0_b': 'tpy4_es_cta0_b',
    '4_es_1_a': 'tpy4_es_cta1_a',
    '4_es_1_b': 'tpy4_es_cta1_b',
    '4_es_0_a_3': 'tpy4_es_cta0_a_paper3',
    '4_es_0_b_3': 'tpy4_es_cta0_b_paper3',
    '4_es_1_a_3': 'tpy4_es_cta1_a_paper3',
    '4_es_1_b_3': 'tpy4_es_cta1_b_paper3',
    '4_fr_0_a': 'tpy4_fr_cta0_a',
    '4_fr_0_b': 'tpy4_fr_cta0_b',
    '4_fr_1_a': 'tpy4_fr_cta1_a',
    '4_fr_1_b': 'tpy4_fr_cta1_b',
    '4_fr_0_a_3': 'tpy4_fr_cta0_a_paper3',
    '4_fr_0_b_3': 'tpy4_fr_cta0_b_paper3',
    '4_fr_1_a_3': 'tpy4_fr_cta1_a_paper3',
    '4_fr_1_b_3': 'tpy4_fr_cta1_b_paper3',
    '4_it_0_a': 'tpy4_it_cta0_a',
    '4_it_0_b': 'tpy4_it_cta0_b',
    '4_it_1_a': 'tpy4_it_cta1_a',
    '4_it_1_b': 'tpy4_it_cta1_b',
    '4_it_0_a_3': 'tpy4_it_cta0_a_paper3',
    '4_it_0_b_3': 'tpy4_it_cta0_b_paper3',
    '4_it_1_a_3': 'tpy4_it_cta1_a_paper3',
    '4_it_1_b_3': 'tpy4_it_cta1_b_paper3',
    '4_en_0_a': 'tpy4_en_cta0_a',
    '4_en_1_a': 'tpy4_en_cta1_a',
    '4_en_0_a_3': 'tpy4_en_cta0_a_paper3',
    '4_en_1_a_3': 'tpy4_en_cta1_a_paper3',

    '6_de_0_a': 'tpy6_de_cta0_a',
    '6_es_0_a': 'tpy6_es_cta0_a',
    '6_fr_0_a': 'tpy6_fr_cta0_a',
    '6_it_0_a': 'tpy6_it_cta0_a',
    '6_en_0_a': 'tpy6_en_cta0_a',
    '6_de_0_a_3': 'tpy6_de_cta0_a_paper3',
    '6_es_0_a_3': 'tpy6_es_cta0_a_paper3',
    '6_fr_0_a_3': 'tpy6_fr_cta0_a_paper3',
    '6_it_0_a_3': 'tpy6_it_cta0_a_paper3',
    '6_en_0_a_3': 'tpy6_en_cta0_a_paper3',

    '7_de_0_a': 'tpy7_de_cta0_a',
    '7_de_1_a': 'tpy7_de_cta1_a',
    '7_es_0_a': 'tpy7_es_cta0_a',
    '7_es_1_a': 'tpy7_es_cta1_a',
    '7_fr_0_a': 'tpy7_fr_cta0_a',
    '7_fr_1_a': 'tpy7_fr_cta1_a',
    '7_it_0_a': 'tpy7_it_cta0_a',
    '7_it_1_a': 'tpy7_it_cta1_a',
    '7_en_0_a': 'tpy7_en_cta0_a',
    '7_en_1_a': 'tpy7_en_cta1_a',

    '8_de_0_a': 'tpy8_de_cta0_a',
    '8_de_1_a': 'tpy8_de_cta1_a',
    '8_es_0_a': 'tpy8_es_cta0_a',
    '8_es_1_a': 'tpy8_es_cta1_a',
    '8_fr_0_a': 'tpy8_fr_cta0_a',
    '8_fr_1_a': 'tpy8_fr_cta1_a',
    '8_it_0_a': 'tpy8_it_cta0_a',
    '8_it_1_a': 'tpy8_it_cta1_a',
    '8_en_0_a': 'tpy8_en_cta0_a',
    '8_en_1_a': 'tpy8_en_cta1_a'
  }

  const key = generateKey({ tpy_type, tpy_lang, tpy_cta, tpy_layout, paper })
  const imageName = imageMapping[key]
  return imageName
}

function generateKey({ tpy_type, tpy_lang, tpy_cta, tpy_layout, paper }) {
  let key = `${tpy_type}_${tpy_lang}_${tpy_cta}_${tpy_layout}`
  if (paper === 3) {
    key += `_${paper}`
  }
  return key
}
